/**
 * Accepted values for apps-api's enumerated component fields, and their unions.
 *
 * - Here, not in `@keboola/api-client`: the OpenAPI spec types these fields as bare `string`.
 * - Values only; human labels live in `constants.ts`.
 */

export const COMPONENT_TYPES = [
  'extractor',
  'writer',
  'transformation',
  'application',
  'processor',
  'code-pattern',
  'data-app',
] as const;
export type ComponentType = (typeof COMPONENT_TYPES)[number];

/**
 * Selectable in edit forms, but not a `COMPONENT_TYPES` member: the public catalog hides `other`.
 */
export const COMPONENT_TYPE_OTHER = 'other';
export type ComponentTypeOrOther = ComponentType | typeof COMPONENT_TYPE_OTHER;

export const FLAG_BETA = 'appInfo.beta';
export const FLAG_EXPERIMENTAL = 'appInfo.experimental';

/**
 * Vendor `type` marking first-party components — what "Supported by Keboola" filters on.
 *
 * - Separate from vendor id `keboola` though equal today: a classification, not a primary key.
 */
export const KEBOOLA_VENDOR_TYPE = 'keboola';

export const REPOSITORY_TYPES = ['ecr', 'quay', 'dockerhub', 'builder'] as const;
export type RepositoryType = (typeof REPOSITORY_TYPES)[number];

export const COMPLEXITIES = ['easy', 'medium', 'hard'] as const;
export type Complexity = (typeof COMPLEXITIES)[number];

export const COMPONENT_CATEGORIES = [
  'API',
  'Accounting',
  'Advertisement',
  'Analytics',
  'CRM',
  'Data Visualization',
  'Database',
  'E-commerce',
  'ERP',
  'File Storage',
  'Marketing',
  'Monitoring',
  'Project Management',
  'Social',
] as const;

export const STAGING_STORAGE_INPUTS = [
  'local',
  's3',
  'workspace-snowflake',
  'workspace-synapse',
  'workspace-redshift',
  'workspace-exasol',
  'workspace-teradata',
  'workspace-bigquery',
  'abs',
  'none',
] as const;
export type StagingStorageInput = (typeof STAGING_STORAGE_INPUTS)[number];

/** Output staging drops `s3` and `abs` — the input-only options. */
export const STAGING_STORAGE_OUTPUTS = [
  'local',
  'workspace-snowflake',
  'workspace-synapse',
  'workspace-redshift',
  'workspace-exasol',
  'workspace-teradata',
  'workspace-bigquery',
  'none',
] as const;
export type StagingStorageOutput = (typeof STAGING_STORAGE_OUTPUTS)[number];

export const COMPONENT_FEATURES = [
  'container-root-user',
  'dev-branch-configuration-unsafe',
  'dev-branch-job-blocked',
  'dev-mapping-allowed',
  'allow-use-file-storage-only',
  'allow-tag-override',
  'has-sample-data',
  'has-simplified-ui',
  'mlflow-artifacts-access',
  'container-tcpkeepalive-60s-override',
  'organization-github-app-auth',
] as const;

export const UI_OPTIONS = [
  'genericDockerUI',
  'genericDockerUI-rows',
  'genericDockerUI-tableInput',
  'genericDockerUI-oneTableInput',
  'genericDockerUI-fileInput',
  'genericDockerUI-tableOutput',
  'genericDockerUI-fileOutput',
  'genericDockerUI-authorization',
  'genericDockerUI-processors',
  'genericDockerUI-resetState',
  'genericDockerUI-simpleTableInput',
  'genericCodeBlocksUI',
  'genericTemplatesUI',
  'genericPackagesUI',
  'appInfo.dataIn',
  'appInfo.dataOut',
  FLAG_EXPERIMENTAL,
  FLAG_BETA,
] as const;

export const DATA_TYPE_SUPPORTS = ['authoritative', 'hints', 'none'] as const;
export type DataTypeSupport = (typeof DATA_TYPE_SUPPORTS)[number];

export const ALLOWED_PROCESSOR_POSITIONS = ['any', 'before', 'after'] as const;
export type AllowedProcessorPosition = (typeof ALLOWED_PROCESSOR_POSITIONS)[number];

export const LOGGERS = ['standard', 'gelf'] as const;
export type Logger = (typeof LOGGERS)[number];

export const DEFAULT_BUCKET_STAGES = ['in', 'out'] as const;
export type DefaultBucketStage = (typeof DEFAULT_BUCKET_STAGES)[number];
